import { oldestFirst } from '~/utils';

import { type DeliveryNoteAdminWithRelatedData } from '../types';

/**
 * Returns billing details sorted newest first and including previouslyBilledAmount for each
 * @param billingDetails - The billing details to sort.
 * @param invoices - Bulk invoice processes to enrich invoice metadata.
 * @returns The sorted billing details.
 */
const getSortedBillingDetails = (
  billingDetails: any[] = [],
  invoices: DeliveryNoteAdminWithRelatedData['bulkInvoices'] = [],
) => {
  // Add the invoice details to the billing details
  const billingDetailsWithInvoiceDetails = [...billingDetails].map(
    (billingDetail) => {
      const invoiceDetails = billingDetail.invoiceUuid
        ? (invoices?.find(
            ({ invoiceId }) => invoiceId === billingDetail.invoiceUuid,
          ) ?? undefined)
        : undefined;

      const result = {
        ...billingDetail,
        invoiceNumber: invoiceDetails?.invoiceNr,
        invoiceProcessId: invoiceDetails?.ivProcessId,
      };

      return result;
    },
  );

  const ascending = oldestFirst(
    billingDetailsWithInvoiceDetails,
    'billedDatetime',
  );

  let sum = 0;
  const withPrevious = ascending.map((current) => {
    const result = { ...current, previouslyBilledAmount: sum };

    sum += Number(current.billedAmount ?? 0);

    return result;
  });

  // Display newest first
  return withPrevious.reverse();
};

/**
 * Gets the billing state details for a line item from the mapped delivery note.
 * @param lineItemIndex - The index of the line item to get the billing state for.
 * @param billedItems - The billed items to get the billing state for.
 * @param invoices - The invoices to get the billing state for.
 * @returns The billing state details for a line item.
 */
export const getLineItemBillingState = (
  lineItemIndex: any,
  billedItems: any,
  invoices: DeliveryNoteAdminWithRelatedData['bulkInvoices'] = [],
) => {
  if (
    !(typeof lineItemIndex === 'number' && lineItemIndex >= 0) ||
    !billedItems?.length
  ) {
    return undefined;
  }

  const billingState = billedItems.find(
    (billedItem) => billedItem.lineItemIndex === lineItemIndex,
  );

  if (!billingState) {
    return undefined;
  }

  const totalBilledQuotient = ![null, undefined].includes(
    billingState?.totalBilledQuotient,
  )
    ? Math.max(0, billingState.totalBilledQuotient)
    : billingState?.totalBilledQuotient;

  const billedPercentage = totalBilledQuotient ? totalBilledQuotient * 100 : 0;

  return {
    ...billingState,
    billedPercentage,
    billingDetails: getSortedBillingDetails(
      billingState?.billingDetails,
      invoices,
    ),
    totalBilledQuotient,
  };
};
